import { z } from 'zod';
import { exportHeaderThemeSchema } from './palier.js';

/** The three export formats (2026-08-09 design doc, decision 3; unchanged here). */
export const exportFormatSchema = z.enum(['excel', 'pdf', 'html']);
export type ExportFormat = z.infer<typeof exportFormatSchema>;

/**
 * Mirrors the frontend's local `ExportSelectionState` shape (2026-08-09
 * design doc, decision 4/5) — now also crossing the wire as part of the
 * export request body, so it needs a shared schema.
 */
export const exportSelectionSchema = z.object({
  includedColumns: z.array(z.number().int()),
  includedDimensionIds: z.array(z.string().uuid()),
  includeActivationRow: z.boolean(),
  /**
   * Whether the exported document names the six activation leviers, and how.
   * `hidden` (default) matches the matrix grid, which shows only the spanning
   * "Ligne d'activation" group label. `column` keeps that label and adds a
   * second label column carrying each levier's `levierValeur`. `inline`
   * replaces the group label with the per-levier names, dropping the span.
   * Lives on `selection` rather than on a format variant because it applies
   * to all three formats.
   */
  activationLevierNames: z.enum(['hidden', 'column', 'inline']).default('hidden'),
  /**
   * Whether the export ends with the notes page (issue #19): the matrix memo,
   * then the open discussions of level `client` with their replies. PMS team
   * and private notes are never exported. Off by default, so an older client
   * that does not send it, and every preset (presets do not carry the
   * selection), export exactly what they used to. Applies to the three formats.
   */
  includeClientNotes: z.boolean().default(false),
});
export type ExportSelection = z.infer<typeof exportSelectionSchema>;

/** 2026-08-16 design doc, decisions C6-C7: one combined toggle + before/after position. */
export const exportSubjectDescriptionOptionsSchema = z.object({
  include: z.boolean(),
  position: z.enum(['before', 'after']),
});
export type ExportSubjectDescriptionOptions = z.infer<typeof exportSubjectDescriptionOptionsSchema>;

/**
 * Decision C3: manual two-way toggle, no automatic orientation. Its default
 * is `landscape` as of decision L2 (2026-08-25 doc) — the default lives in
 * `DEFAULT_PAGE_OPTIONS`, not here.
 */
export const exportOrientationSchema = z.enum(['portrait', 'landscape']);
export type ExportOrientation = z.infer<typeof exportOrientationSchema>;

/**
 * The PDF/HTML export option blocks — export-rework chantier 1 (spec R1-R5).
 * `page` is PDF-only; `table`, `dressing` and `appearance` are shared by both
 * PDF and HTML (spec R2-R5), and excel carries `table` + `appearance` too
 * (issue #12, decision X1).
 *
 * These superseded, and as of Task 10 replaced outright, the transitional
 * `pdfOptions`/`scale`/top-level `headerTheme` surface. A stale client still
 * sending those keys is accepted and they are ignored — Zod strips unknown
 * keys rather than rejecting — so an old tab keeps exporting, at the new
 * defaults rather than at its own retired settings.
 */
export const exportPageFormatSchema = z.enum(['a4', 'a3']); // spec R1
export const exportMarginsSchema = z.enum(['minimales', 'normales', 'larges']); // spec R1
export const exportFitSchema = z.enum([
  'aucun',
  'une-page-de-large',
  'une-page-de-haut',
  'une-seule-page',
]); // spec R1
export type ExportFit = z.infer<typeof exportFitSchema>;
export const exportDensitySchema = z.enum(['compact', 'normal', 'aere']); // spec R2
export const exportRowHeightModeSchema = z.enum(['contenu', 'ecran']); // spec R2
export const exportTableStructureSchema = z.enum(['colonnes', 'transpose']); // spec R2
export const exportPmsSignatureSchema = z.enum(['genere-avec', 'wordmark', 'anneau', 'aucune']); // spec R4

export const exportPageOptionsSchema = z.object({
  format: exportPageFormatSchema,
  orientation: exportOrientationSchema,
  margins: exportMarginsSchema,
  fontSizePt: z.number().int().min(8).max(14),
  fit: exportFitSchema,
});
export type ExportPageOptions = z.infer<typeof exportPageOptionsSchema>;

export const exportTableOptionsSchema = z.object({
  density: exportDensitySchema,
  rowHeightMode: exportRowHeightModeSchema,
  repeatHeaders: z.boolean(),
  structure: exportTableStructureSchema,
  /**
   * Per-palier width overrides in unscaled CSS px, keyed by palier number as
   * string. Missing = viewState/screen width.
   *
   * Defaulted via a factory (`() => ({})`), not a bare `{}`: Zod v3 wraps a
   * non-function `.default(...)` value as `() => value`, so a literal `{}`
   * here would be the *same* object reference handed back on every parse
   * where this field is absent — a shared-mutable-default hazard.
   */
  columnWidths: z.record(z.string(), z.number().min(40).max(1200)).default(() => ({})),
  /**
   * Palier numbers in display order; empty = natural ascending order.
   *
   * These two arrays are written by the viewer's « Aménager » drags (issue
   * #11), so they are capped like every other client-composed list: far above
   * any real matrix (a handful of paliers, a few dozen rows) and far below
   * what would make the tiling loop over an attacker-sized input.
   */
  columnOrder: z
    .array(z.number().int())
    .max(64)
    .default(() => []),
  /** Row keys (levier order '1'-'6' or dimension UUID) that start a new page. */
  pageBreaksBefore: z
    .array(z.string().max(200))
    .max(500)
    .default(() => []),
});
export type ExportTableOptions = z.infer<typeof exportTableOptionsSchema>;

export const exportDressingOptionsSchema = z.object({
  companyLogo: z.boolean(),
  titleBlock: z.boolean(),
  dates: z.boolean(),
  footerText: z.string().max(200).nullable(),
  pageNumbers: z.boolean(),
  pmsSignature: exportPmsSignatureSchema,
  assemblyMarks: z.boolean(),
});
export type ExportDressingOptions = z.infer<typeof exportDressingOptionsSchema>;

export const exportAppearanceOptionsSchema = z.object({
  headerTheme: exportHeaderThemeSchema, // moves INTO appearance (spec R5)
  borders: z.enum(['fines', 'marquees']),
  zebra: z.boolean(),
});
export type ExportAppearanceOptions = z.infer<typeof exportAppearanceOptionsSchema>;

/** « Lecture » preset = the wire defaults (spec P2). */
export const DEFAULT_PAGE_OPTIONS: ExportPageOptions = {
  format: 'a4',
  orientation: 'landscape',
  margins: 'normales',
  fontSizePt: 10,
  fit: 'aucun',
};
export const DEFAULT_TABLE_OPTIONS: ExportTableOptions = {
  density: 'normal',
  rowHeightMode: 'contenu',
  repeatHeaders: true,
  structure: 'colonnes',
  columnWidths: {},
  columnOrder: [],
  pageBreaksBefore: [],
};
export const DEFAULT_DRESSING_OPTIONS: ExportDressingOptions = {
  companyLogo: true,
  titleBlock: true,
  dates: true,
  footerText: null,
  pageNumbers: true,
  pmsSignature: 'genere-avec',
  assemblyMarks: false,
};
export const DEFAULT_APPEARANCE_OPTIONS: ExportAppearanceOptions = {
  headerTheme: 'pitch',
  borders: 'fines',
  zebra: false,
};

/**
 * Content-anchored annotations on the wire — decisions A1-A3/A9.
 *
 * Zod-validated because they cross a trust boundary: the browser composes them
 * and the backend renders them into a document. The TypeScript interfaces in
 * `export-render/export-annotations.ts` are inferred from these, so the wire
 * contract and the projection math cannot drift apart.
 *
 * An ink stroke's path is capped: a stroke is sampled per pointer event, so a
 * long slow drag can produce thousands of points, and an unbounded array here
 * would be an easy way to make the server render an enormous SVG.
 */
export const exportInkAnnotationSchema = z.object({
  kind: z.literal('ink'),
  id: z.string().min(1).max(64),
  rowKey: z.string().min(1).max(200),
  columnNumber: z.number().int(),
  originX: z.number().min(0).max(1),
  originY: z.number().min(0).max(1),
  points: z
    .array(z.object({ dx: z.number(), dy: z.number() }))
    .min(1)
    .max(5_000),
});

export const exportHighlightAnnotationSchema = z.object({
  kind: z.literal('highlight'),
  id: z.string().min(1).max(64),
  rowKey: z.string().min(1).max(200),
  columnNumber: z.number().int(),
  lineIndex: z.number().int().min(0),
});

export const exportAnnotationSchema = z.discriminatedUnion('kind', [
  exportInkAnnotationSchema,
  exportHighlightAnnotationSchema,
]);
export type ExportInkAnnotation = z.infer<typeof exportInkAnnotationSchema>;
export type ExportHighlightAnnotation = z.infer<typeof exportHighlightAnnotationSchema>;
export type ExportAnnotation = z.infer<typeof exportAnnotationSchema>;

/** Defaulted to empty so a client that never annotates needs no new field. */
const annotationsField = z.array(exportAnnotationSchema).max(2_000).default([]);

/**
 * `POST /matrices/:id/export` body. Discriminated on `format` so each
 * variant only carries the fields that format actually uses — excel has no
 * document framing or pagination at all (decision 8 of the 2026-08-09 doc,
 * still true), html has framing but no pagination (decision G5/C-series of
 * the 2026-08-16 doc), pdf has both.
 *
 * As of export-rework chantier 4 (issue #12, decision X1) excel is styled
 * like the matrix, so it carries the `table` and `appearance` blocks — but
 * still no `dressing`: spec F1 rejected a « classeur document », so a
 * `dressing` key sent on an excel body is stripped like any unknown key.
 * Both blocks default like html/pdf, so a legacy `{ format, selection }`
 * body still parses and simply gets the « Lecture » defaults.
 */
export const exportRequestSchema = z.discriminatedUnion('format', [
  z.object({
    format: z.literal('excel'),
    selection: exportSelectionSchema,
    // Cloning factories, as on the html variant below.
    table: exportTableOptionsSchema.default(() => structuredClone(DEFAULT_TABLE_OPTIONS)),
    appearance: exportAppearanceOptionsSchema.default(() =>
      structuredClone(DEFAULT_APPEARANCE_OPTIONS),
    ),
  }),
  z.object({
    format: z.literal('html'),
    selection: exportSelectionSchema,
    subjectDescription: exportSubjectDescriptionOptionsSchema,
    /** Decision A6: html carries annotations too — only excel cannot. */
    annotations: annotationsField,
    // `.default(() => structuredClone(DEFAULT_*))`, not `.default(DEFAULT_*)`:
    // Zod v3 wraps a non-function `.default(...)` value as `() => value`, so
    // passing the constant directly would hand back the *same* object
    // reference on every parse where the field is absent — and
    // `DEFAULT_TABLE_OPTIONS` carries mutable containers (`columnWidths`,
    // `columnOrder`, `pageBreaksBefore`), so an in-place mutation of one
    // parsed result would silently corrupt the exported constant (and every
    // other already-parsed request) process-wide. The factory clones fresh
    // per parse instead.
    table: exportTableOptionsSchema.default(() => structuredClone(DEFAULT_TABLE_OPTIONS)),
    dressing: exportDressingOptionsSchema.default(() => structuredClone(DEFAULT_DRESSING_OPTIONS)),
    appearance: exportAppearanceOptionsSchema.default(() =>
      structuredClone(DEFAULT_APPEARANCE_OPTIONS),
    ),
  }),
  z.object({
    format: z.literal('pdf'),
    selection: exportSelectionSchema,
    subjectDescription: exportSubjectDescriptionOptionsSchema,
    /** Decision A9: baked into the document by the same renderer that draws the grid. */
    annotations: annotationsField,
    // See the html variant above — cloning factories, not bare constants, to
    // avoid Zod v3's shared-mutable-default aliasing.
    page: exportPageOptionsSchema.default(() => structuredClone(DEFAULT_PAGE_OPTIONS)),
    table: exportTableOptionsSchema.default(() => structuredClone(DEFAULT_TABLE_OPTIONS)),
    dressing: exportDressingOptionsSchema.default(() => structuredClone(DEFAULT_DRESSING_OPTIONS)),
    appearance: exportAppearanceOptionsSchema.default(() =>
      structuredClone(DEFAULT_APPEARANCE_OPTIONS),
    ),
  }),
]);
export type ExportRequest = z.infer<typeof exportRequestSchema>;

/**
 * The facts about a rendered PDF that only the server knows - whether the
 * company logo was fetched, which display dates were printed (issue #11) and
 * how many pages the notes section added (issue #19). Everything else about
 * the document's geometry follows from the request, so with these the browser re-runs `planPdfDocument` on exactly
 * the document Chromium drew and its overlays land on the right cells.
 *
 * Validated on the browser side (`safeParse` of the response header): it is
 * an external input like any other API response.
 */
export const exportRenderFactsSchema = z.object({
  hasCompanyLogo: z.boolean(),
  exportedAt: z.string().nullable(),
  matrixUpdatedAt: z.string().nullable(),
  /**
   * How many pages the « Notes » section added at the end (issue #19) - 0 when
   * the export carries none, and for a header written before the notes
   * existed. Only the server knows it: it depends on the notes it loaded.
   */
  notesPageCount: z.number().int().min(0).default(0),
});
export type ExportRenderFacts = z.infer<typeof exportRenderFactsSchema>;

/** Response header carrying the PDF branch's `ExportRenderFacts`, as JSON. */
export const EXPORT_RENDER_FACTS_HEADER = 'X-Export-Render-Facts';
